import { useMemo, useState } from 'react';
import { Alert, Box, Paper, Skeleton, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import ListEmptyState from './ListEmptyState';
import { queryPackingListItems, queryItemTypesWithLocations, PackingListItem, ItemType, EventDetails, InventoryHistory } from '../schema';
import ConsumptionRow from './ConsumptionRow';
import RecordConsumptionDialog from './RecordConsumptionDialog';
import { db } from '../instant';

interface Props {
    eventId: string;
    event: EventDetails;
}

interface ConsumableItemData {
    itemType: ItemType;
    itemLocation: { id: string; quantity: number } | null;
    packingListItem: PackingListItem | null;
    consumptionHistory: InventoryHistory | null;
    totalTaken: number;
}

export default function EventInventoryConsumables({ eventId, event }: Props) {
    const { data: packingListData, isLoading: packingListLoading } = queryPackingListItems(eventId);
    const { data: itemTypesData, isLoading: itemTypesLoading } = queryItemTypesWithLocations();
    const [recordDialogOpen, setRecordDialogOpen] = useState(false);
    const [selectedItemType, setSelectedItemType] = useState<ItemType | null>(null);
    const [selectedHistory, setSelectedHistory] = useState<InventoryHistory | null>(null);

    // Query consumption history for all consumable items at this event
    // We'll query all inventory history and filter client-side
    const { data: allHistoryData } = db.useQuery({
        inventoryHistory: {
            $: {
                where: {
                    'event.id': eventId,
                    type: 'consumed',
                },
            },
            itemType: {
                category: {},
            },
            event: {},
        },
    });

    const isLoading = packingListLoading || itemTypesLoading;

    // Build consumable items data
    const consumableItems = useMemo<ConsumableItemData[]>(() => {
        if (!itemTypesData?.itemTypes) return [];

        const packingListItems = (packingListData?.events?.[0]?.packingListItems || []) as PackingListItem[];
        const packingListByItemType = new Map<string, PackingListItem>();
        for (const item of packingListItems) {
            if (item.itemType?.id) {
                packingListByItemType.set(item.itemType.id, item);
            }
        }

        const historyByItemType = new Map<string, InventoryHistory>();
        const history = (allHistoryData?.inventoryHistory || []) as InventoryHistory[];
        for (const h of history) {
            if (h.itemType?.id) {
                historyByItemType.set(h.itemType.id, h);
            }
        }

        const items: ConsumableItemData[] = [];

        for (const itemType of itemTypesData.itemTypes) {
            // Only include consumable items
            if (!itemType.consumable) continue;

            // Find itemLocation at this event
            const itemLocations = (itemType as any).itemLocations || [];
            const eventLocation = itemLocations.find((loc: any) => loc.event?.id === eventId);
            const itemLocation = eventLocation ? { id: eventLocation.id, quantity: eventLocation.quantity || 0 } : null;

            // Find packing list item
            const packingListItem = packingListByItemType.get(itemType.id) || null;

            // Find consumption history
            const consumptionHistory = historyByItemType.get(itemType.id) || null;

            // Calculate total taken: prefer packing list quantity, fallback to itemLocation quantity
            const totalTaken = packingListItem?.quantity || itemLocation?.quantity || 0;

            // Only show items that have been taken to the event (have packing list item or location at event)
            if (totalTaken > 0) {
                items.push({
                    itemType,
                    itemLocation,
                    packingListItem,
                    consumptionHistory,
                    totalTaken,
                });
            }
        }

        // Sort by name
        items.sort((a, b) => a.itemType.name.localeCompare(b.itemType.name));

        return items;
    }, [itemTypesData, packingListData, allHistoryData, eventId]);

    const itemsWithConsumption = useMemo(() => consumableItems.filter(item => item.consumptionHistory !== null).length, [consumableItems]);

    const handleRecord = (itemType: ItemType, history: InventoryHistory | null) => {
        setSelectedItemType(itemType);
        setSelectedHistory(history);
        setRecordDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setRecordDialogOpen(false);
        setSelectedItemType(null);
        setSelectedHistory(null);
    };

    if (isLoading) {
        return (
            <Stack spacing={2}>
                <Skeleton variant="rectangular" height={24} width="60%" sx={{ borderRadius: 1 }} />
                <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
            </Stack>
        );
    }

    return (
        <Box>
            {/* Section Header */}
            <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {consumableItems.length} {consumableItems.length === 1 ? 'consumable item' : 'consumable items'} at this event
                </Typography>
                {itemsWithConsumption > 0 && (
                    <Typography variant="body2" color="text.secondary">
                        {itemsWithConsumption} {itemsWithConsumption === 1 ? 'item has' : 'items have'} consumption recorded
                    </Typography>
                )}
            </Box>

            {consumableItems.length === 0 ? (
                <ListEmptyState
                    icon={<LocalGasStationIcon sx={{ fontSize: 48 }} />}
                    message="No consumable items at this event"
                    subtext="Add consumable items to the packing list to track their usage"
                />
            ) : (
                <>
                    {itemsWithConsumption === 0 && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            No consumption recorded yet. Use the + button in the Actions column to track usage.
                        </Alert>
                    )}
                    <Paper
                        sx={{
                            overflowX: 'auto',
                            width: '100%',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                            borderRadius: '8px',
                            border: '1px solid rgba(0,0,0,0.06)',
                        }}
                    >
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Photo</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Taken</TableCell>
                                    <TableCell>Used</TableCell>
                                    <TableCell>Remaining</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {consumableItems.map(item => (
                                    <ConsumptionRow
                                        key={item.itemType.id}
                                        itemType={item.itemType}
                                        event={event}
                                        itemLocation={item.itemLocation}
                                        consumptionHistory={item.consumptionHistory}
                                        totalTaken={item.totalTaken}
                                        onRecord={() => handleRecord(item.itemType, null)}
                                        onEdit={() => handleRecord(item.itemType, item.consumptionHistory)}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                </>
            )}

            {selectedItemType && (
                <RecordConsumptionDialog
                    open={recordDialogOpen}
                    onClose={handleCloseDialog}
                    itemType={selectedItemType}
                    event={event}
                    existingHistory={selectedHistory}
                    existingItemLocation={consumableItems.find(item => item.itemType.id === selectedItemType.id)?.itemLocation ?? null}
                    totalTaken={consumableItems.find(item => item.itemType.id === selectedItemType.id)?.totalTaken || 0}
                />
            )}
        </Box>
    );
}
